import { createAsyncThunk } from "@reduxjs/toolkit";

import { ChatStatus, ChatType, type ChatChannelWithLastMessage } from "@fitness-app/data-models/entities/Chat";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AsyncThunkCreator } from "../../../index";
import { getParentTrainerId, getUserClaims } from "../../user/selectors";
import { addChannelToList } from "../reducer";
import { CHANNEL_NOT_FOUND_ERROR, CHAT_REDUCER_NAME } from "../types";

type Payload = {
  userId: string;
  channelId?: string;
  isHeadCoach?: boolean;
  /** "marketing" opens the client's announcement channel (TRE-801); default = regular chat. */
  channelType?: "default" | "marketing";
};

export const findAndAddChannelToList = createAsyncThunk<ChatChannelWithLastMessage, Payload, AsyncThunkCreator<string>>(
  `${CHAT_REDUCER_NAME}/findAndAddChannelToList`,
  async (
    { userId, channelId, isHeadCoach, channelType = "default" },
    { rejectWithValue, getState, dispatch, extra: { db, auth } },
  ) => {
    const { channels } = getState().chat;
    const parentTrainerId = getParentTrainerId(getState());
    // Klienckie kanały mają w assignedUsers właściciela konta, nie personel —
    // administracja (jak head coach) szuka więc pary [klientka, właściciel].
    const { isAdministration } = getUserClaims(getState());
    const actsForTenant = Boolean(isHeadCoach) || isAdministration;

    const matchedTypes =
      channelType === "marketing"
        ? [ChatType.OneWayMarketing, ChatType.TwoWayMarketing]
        : [ChatType.OneWay, ChatType.TwoWay];

    const foundedChannel = channels.find((channel) =>
      channelId
        ? channel.id === channelId && matchedTypes.includes(channel.type)
        : channel.assignedUsers.includes(userId) && matchedTypes.includes(channel.type),
    );

    if (foundedChannel) {
      dispatch(addChannelToList(foundedChannel));
      return foundedChannel;
    }

    const currentUser = await getLoggedUser(auth);

    let query = db
      .from("chat_channel")
      .select(
        "*, lastMessage:lastMessageId(id, content, createdAt, files), profiles:user_profile(avatarUrl, firstName, lastName, id, email))",
      )
      .order("updatedAt", { ascending: false })
      // Status kanału nie idzie w parze ze statusem klientki (na prodzie 109
      // zarchiwizowanych klientek ma wciąż `active`, a 2 aktywne — `archived`).
      // Wyliczanie go z `trainee.status` po stronie wywołania kończyło się
      // CHANNEL_NOT_FOUND_ERROR, więc bierzemy kanał niezależnie od statusu.
      .in("status", [ChatStatus.Active, ChatStatus.Archived])
      .in("type", matchedTypes)
      .limit(1);

    // Kanał ogłoszeniowy ma dokładnie jedną klientkę i stemplowaną kolumnę
    // clientUserId (TRE-1945) — lookup po niej jest niezależny od tego, kto pyta.
    query =
      channelType === "marketing"
        ? query.eq("clientUserId", userId)
        : query.contains("assignedUsers", [userId, actsForTenant ? parentTrainerId : currentUser.id]);

    const { error, data } = await query.maybeSingle<ChatChannelWithLastMessage>();

    if (error) {
      return rejectWithValue(error.message);
    }

    if (!data) {
      // `single()` used to turn "no such channel" into a raw PGRST116 postgres error here.
      return rejectWithValue(CHANNEL_NOT_FOUND_ERROR);
    }

    dispatch(addChannelToList(data));

    return data;
  },
);
